/**
 * Full-page fallback of the app's ErrorBoundary, in the dot-matrix system (auth shell look:
 * white page, orange top rule, brand mark, one pixel figure, orange primary action).
 *
 * Replaces the old Tailwind block (red headline, green WhatsApp button, raw error as body copy).
 * The raw message stays available under "Technical details" with a copy button, because support
 * asks for it — but it is no longer the first thing a customer reads.
 * Self-contained on purpose: no router, no data, no lazy imports — it has to render when the
 * rest of the app just failed.
 */
import React, { useState } from 'react';
import { Bot, RotateCw, MessageCircle, ArrowRight, Copy, Check } from 'lucide-react';
import { brandConfig } from '../config/brand';
import { isWhiteLabel, shopLogo } from '../config/resellerShop';
import './ErrorScreen.css';

interface ErrorScreenProps {
  error: Error | null;
  supportUrl?: string | null;
}

// The brand robot, knocked out: X eyes, wavy mouth. Same dot grammar as the sprite set.
const ROBOT_KO = [
  '......#......',
  '......#......',
  '.###########.',
  '##.........##',
  '##.#.#.#.#.##',
  '##..#...#..##',
  '##.#.#.#.#.##',
  '##.........##',
  '##..#.#.#..##',
  '##...#.#...##',
  '.###########.',
];
const FIGURE = ROBOT_KO.map((row) => row.split('').map((c) => c === '#'));

const ErrorScreen: React.FC<ErrorScreenProps> = ({ error, supportUrl }) => {
  const [copied, setCopied] = useState(false);
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const inDashboard = path.startsWith('/dashboard') || path.startsWith('/admin');
  const at = new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
  const details = `${error?.name || 'Error'}: ${error?.message || 'Unknown error'}\nPage: ${path}\nTime: ${at}`;
  const brandName = brandConfig.id === 'trafficcreator' ? 'Traffic Creator' : brandConfig.name;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the text is still visible and selectable.
    }
  };

  return (
    <div className="tc-err" role="alert">
      <header className="tc-err__top">
        <a href="/" className="tc-err__brand" aria-label={`${brandName} home`}>
          <span className="tc-err__mark" aria-hidden="true">
            {isWhiteLabel()
              ? (shopLogo() ? <img src={shopLogo()!} alt="" /> : <b>{brandConfig.logoLetter}</b>)
              : <Bot size={25} strokeWidth={2.4} />}
          </span>
          <span className="tc-err__name">
            {brandConfig.id === 'trafficcreator' ? <>TRAFFIC <strong>CREATOR</strong></> : brandConfig.name}
          </span>
        </a>
      </header>

      <main className="tc-err__main">
        <div className="tc-err__figure" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${FIGURE[0].length}, var(--err-dot-size))` }}>
          {FIGURE.map((row, y) => row.map((on, x) => <i key={`${x}-${y}`} className={on ? 'is-on' : undefined} />))}
        </div>

        <p className="tc-err__eyebrow"><span /> Error on this page</p>
        <h1 className="tc-err__title">This page hit a <em>snag.</em></h1>
        <p className="tc-err__lead">
          Nothing is lost — your account, campaigns and credits are safe, and running campaigns keep delivering.
          Reloading the page fixes this in most cases.
        </p>

        <div className="tc-err__actions">
          <button type="button" className="tc-err__btn tc-err__btn--primary" onClick={() => window.location.reload()}>
            <RotateCw size={17} strokeWidth={2.4} aria-hidden="true" /> Reload page
          </button>
          {supportUrl && (
            <a className="tc-err__btn tc-err__btn--ghost" href={supportUrl} target="_blank" rel="noopener noreferrer">
              <MessageCircle size={17} strokeWidth={2.2} aria-hidden="true" /> Get help on WhatsApp
            </a>
          )}
        </div>
        <a className="tc-err__link" href={inDashboard ? '/dashboard' : '/'}>
          {inDashboard ? 'Back to the dashboard' : 'Back to the homepage'} <ArrowRight size={15} strokeWidth={2.4} aria-hidden="true" />
        </a>

        <details className="tc-err__details">
          <summary>Technical details</summary>
          <pre>{details}</pre>
          <button type="button" className="tc-err__copy" onClick={copy}>
            {copied ? <><Check size={14} aria-hidden="true" /> Copied</> : <><Copy size={14} aria-hidden="true" /> Copy for support</>}
          </button>
        </details>
      </main>

      <footer className="tc-err__foot">
        <span>© {new Date().getFullYear()} {brandName}</span>
        {brandConfig.supportEmail && <a href={`mailto:${brandConfig.supportEmail}`}>{brandConfig.supportEmail}</a>}
      </footer>
    </div>
  );
};

export default ErrorScreen;
